// src/components/practice/tasks/WritingTask.jsx - FIXED WITH TASK SELECTION
import React, { useState, useEffect, useRef } from 'react';
import PaperExamStyle from './PaperExamStyle';
import { marked } from 'marked';

const WritingTask = ({ partData, savedAnswers = {}, onAnswerChange, onBackToSelection, selectedTaskIndex: propSelectedTaskIndex, goToNextPart, images = [] }) => {
    const hasMultipleTasks = partData.tasks && partData.tasks.length > 1 && partData.optional;
    const isGoethePart = partData.examProvider === 'Goethe';

    // ALL HOOKS MUST BE DECLARED FIRST - BEFORE ANY CONDITIONAL LOGIC OR RETURNS
    const [localSelectedTaskIndex, setLocalSelectedTaskIndex] = useState(propSelectedTaskIndex ?? null);
    const [userText, setUserText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const saveTimeoutRef = useRef(null);

    // Use local state if prop is undefined/null
    const selectedTaskIndex = propSelectedTaskIndex ?? localSelectedTaskIndex;
    const showTaskSelection = hasMultipleTasks && selectedTaskIndex === null;

    console.log('WritingTask Render:', {
        provider: partData.examProvider,
        partId: partData.partId,
        hasMultipleTasks,
        selectedTaskIndex,
        showTaskSelection,
        tasksLength: partData.tasks?.length,
        savedAnswersKeys: Object.keys(savedAnswers),
        isGoethePart,
        taskType: partData.taskType
    });

    // Handle task selection
    const handleTaskSelect = (index) => {
        setLocalSelectedTaskIndex(index);
    };

    const getCurrentTask = () => {
        if (hasMultipleTasks && selectedTaskIndex !== null) {
            return partData.tasks[selectedTaskIndex] || partData.tasks[0];
        }
        return partData.task || (partData.tasks ? partData.tasks[0] : {});
    };

    const currentTask = getCurrentTask();

    // FIXED: Better key generation that works for both Goethe (separate parts) and OSD (multiple tasks)
    const getTaskTextKey = () => {
        const provider = partData.examProvider || 'default';
        const partId = partData.partId || 'part';

        if (hasMultipleTasks && selectedTaskIndex !== null) {
            // Multiple tasks in one part (OSD/TELC style)
            return `${provider}_${partId}_task${selectedTaskIndex}_text`;
        } else {
            // Single task per part (Goethe style) or single task overall
            if (isGoethePart) {
                return `${provider}_${partId}_text`;
            } else {
                return `${provider}_${partId}_writingText`;
            }
        }
    };

    const taskTextKey = getTaskTextKey();

    console.log('Task Key Generated:', {
        taskTextKey,
        partId: partData.partId,
        provider: partData.examProvider,
        hasMultipleTasks,
        selectedTaskIndex,
        isGoethePart
    });

    // Load text when component mounts or key changes
    useEffect(() => {
        const savedTextForCurrentTask = savedAnswers[taskTextKey] || '';

        console.log('Loading text for task:', {
            provider: partData.examProvider,
            partId: partData.partId,
            taskKey: taskTextKey,
            hasSavedText: !!savedTextForCurrentTask,
            textPreview: savedTextForCurrentTask.substring(0, 50),
            allSavedKeys: Object.keys(savedAnswers)
        });

        setUserText(savedTextForCurrentTask);
    }, [taskTextKey, partData.partId, savedAnswers]);

    // Enhanced save function
    const saveAnswers = (currentText) => {
        console.log('Saving text:', {
            provider: partData.examProvider,
            partId: partData.partId,
            taskKey: taskTextKey,
            textLength: currentText.length,
            hasOnAnswerChange: typeof onAnswerChange === 'function'
        });

        // CRITICAL: Check if onAnswerChange exists before calling
        if (typeof onAnswerChange !== 'function') {
            console.error('onAnswerChange is not a function!', {
                onAnswerChange,
                partId: partData.partId,
                taskKey: taskTextKey
            });
            return;
        }

        const updatedAnswers = {
            ...savedAnswers,
            [taskTextKey]: currentText
        };
        const taskDataKey = `${partData.partId}_taskData`;
        updatedAnswers[taskDataKey] = {
            scenario: currentTask.scenario || partData.task?.scenario || null,
            guidingPoints: currentTask.guidingPoints || partData.task?.guidingPoints || [],
            inputContent: currentTask.inputContent || partData.task?.inputContent || null,
            requirements: currentTask.requirements || partData.task?.requirements || []
        };
        // For Goethe, also save under a backup key for compatibility
        if (isGoethePart) {
            updatedAnswers.writingText = currentText;
        }

        // For multiple tasks (OSD style)
        if (hasMultipleTasks && selectedTaskIndex !== null) {
            updatedAnswers.selectedTaskIndex = selectedTaskIndex;
            updatedAnswers.hasMultipleTasks = true;

            if (!updatedAnswers.allTaskTexts) {
                updatedAnswers.allTaskTexts = {};
            }

            updatedAnswers.allTaskTexts[selectedTaskIndex] = {
                text: currentText,
                taskId: currentTask.id || selectedTaskIndex,
                taskType: currentTask.taskType || partData.taskType,
                wordCount: currentText.trim().split(/\s+/).filter(Boolean).length,
                provider: partData.examProvider,
                partId: partData.partId,
                taskData: {
                    scenario: currentTask.scenario || null,
                    guidingPoints: currentTask.guidingPoints || [],
                    inputContent: currentTask.inputContent || null,
                    requirements: currentTask.requirements || []
                }
            };
        }

        try {
            onAnswerChange(partData.partId, updatedAnswers);
            console.log('Successfully saved answers for:', partData.partId);
        } catch (error) {
            console.error('Error saving answers:', error, {
                partId: partData.partId,
                taskKey: taskTextKey
            });
        }
    };

    // Auto-save effect
    useEffect(() => {
        if (!userText) return;

        const timer = setTimeout(() => {
            saveAnswers(userText);
        }, 300000);

        return () => clearTimeout(timer);
    }, [userText, taskTextKey]);

    const handleTextChange = (e) => {
        const newText = e.target.value;
        setUserText(newText);

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            saveAnswers(newText);
        }, 500);
    };

   const handleImmediateSaveAndContinue = () => {
       console.log('🔵 BUTTON CLICKED - handleImmediateSaveAndContinue called', {
        isSubmitting,
        hasGoToNextPart: !!goToNextPart,
        currentPartId: partData.partId,
        userTextLength: userText.length
    });
    if (isSubmitting) return;

    setIsSubmitting(true);
    saveAnswers(userText);

    // Small delay to ensure state is saved before navigation
    setTimeout(() => {
        if (goToNextPart) {
            goToNextPart();
        }
        // Reset submitting state after navigation
        setIsSubmitting(false);
    }, 100);
};

    const getWordLimit = () => {
        if (currentTask?.wordLimit) return currentTask.wordLimit;
        if (currentTask?.wordCount) return { target: currentTask.wordCount };
        if (partData.wordLimit) return partData.wordLimit;

        // FIXED: Default word limits for Goethe B2
        if (isGoethePart && partData.level === 'B2') {
            if (partData.partId === 'writing-task-1' || partData.taskType === 'forum-post') {
                return { min: 150 };
            } else if (partData.partId === 'writing-task-2' || partData.taskType === 'formal-message') {
                return { min: 100 };
            }
        }

        return null;
    };

    const enhancedPartData = {
        ...partData,
        task: {
            ...currentTask,
            wordLimit: getWordLimit(),
            taskIndex: selectedTaskIndex || 0,
            taskId: currentTask.id || (selectedTaskIndex || 0)
        },
        hasMultipleTasks: hasMultipleTasks,
        selectedTaskIndex: selectedTaskIndex || 0,
        totalTasks: partData.tasks ? partData.tasks.length : 1
    };

   const commonProps = {
    partData: enhancedPartData,
    userText,
    handleTextChange,
    onBackToSelection: onBackToSelection,  // ✅ Pass the prop directly from PartDisplay
    onSaveAndContinue: handleImmediateSaveAndContinue,
    isSubmitting: isSubmitting,
    images: images || []
};

    // CONDITIONAL RETURN AT THE VERY END - AFTER ALL HOOKS AND LOGIC
    if (showTaskSelection && hasMultipleTasks) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 sm:p-12">
                    {/* Part Name Header */}
                    <h2 className="text-xl font-bold text-gray-900 mb-4">
                        {partData.partName || "Wahlaufgabe"}
                    </h2>

                    {/* Instructions */}
                    {partData.instructions && (
                        <div className="mb-6">
                            <p className="text-gray-800 italic text-base">
                                {partData.instructions}
                            </p>
                        </div>
                    )}

                    {/* Dividing Line */}
                    <hr className="border-t-2 border-gray-300 my-6" />

                    {/* Task Options - Exam Paper Style */}
                    <div className="space-y-0">
                        {partData.tasks.map((task, index) => (
                            <div key={task.id || index}>
                                <div className="py-6">
                                    {/* Task Letter (Bold) */}
                                    <p className="font-bold text-gray-900 text-lg mb-3">
                                        {task.name || task.id}
                                    </p>

                                    {/* Task Description */}
                                    <div className="text-gray-800 leading-relaxed mb-3 text-base"
                                         dangerouslySetInnerHTML={{ __html: marked.parse(task.scenario || '') }} />

                                    {/* Task Instruction (Italic) with Word Count */}
                                    {(task.instruction || task.wordLimit) && (
                                        <p className="text-gray-700 italic text-base mb-4">
                                            {task.instruction && task.instruction}
                                            {task.wordLimit && (
                                                <span>
                                                    {task.instruction && ' '}
                                                    {task.wordLimit.min
                                                        ? `(mindestens ${task.wordLimit.min} Wörter)`
                                                        : `(ca. ${task.wordLimit.target || task.wordLimit} Wörter)!`}
                                                </span>
                                            )}
                                        </p>
                                    )}

                                    {/* Selection Button with Lift Animation */}
                                    <button
                                        onClick={() => handleTaskSelect(index)}
                                        className="inline-flex items-center px-8 py-3 border-2 border-gray-900 text-base font-semibold rounded-md text-gray-900 bg-white cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:scale-105"
                                    >
                                        Diese Aufgabe wählen
                                    </button>
                                </div>

                                {/* Dividing Line (except after last item) */}
                                {index < partData.tasks.length - 1 && (
                                    <hr className="border-t border-gray-300" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return <PaperExamStyle {...commonProps} />;
};

export default WritingTask;